"use client"

import { useMemo, useState } from "react"

import { useTranslations } from "next-intl"

import { Button, Checkbox, ChevronIcon, SearchInput, Stack, Text } from "@/components/ui"
import type { FacetValue } from "@/lib/types/search"

interface BrandFacetProps {
  options: FacetValue[]
  selected: string[]
  onToggle: (value: string) => void
}

const ROW = "hover:bg-surface-newgray -mx-200 rounded-6x flex px-200 py-150"

const COLLAPSED_COUNT = 8

const SEARCHABLE_COUNT = 12

const fold = (value: string) =>
  value
    .toLocaleLowerCase("es-MX")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")

export const BrandFacet = ({ options, selected, onToggle }: BrandFacetProps) => {
  const t = useTranslations("search")
  const [showAll, setShowAll] = useState(false)
  const [term, setTerm] = useState("")

  const ordered = useMemo(
    () => [...options.filter((option) => selected.includes(option.value)), ...options.filter((option) => !selected.includes(option.value))],
    [options, selected]
  )

  const matches = useMemo(() => {
    const needle = fold(term.trim())
    return needle ? ordered.filter((option) => fold(option.label).includes(needle)) : ordered
  }, [ordered, term])

  const forceExpand = matches.slice(COLLAPSED_COUNT).some((option) => selected.includes(option.value))
  const collapsed = !showAll && !forceExpand && !term && matches.length > COLLAPSED_COUNT
  const visible = collapsed ? matches.slice(0, COLLAPSED_COUNT) : matches
  const hiddenCount = matches.length - COLLAPSED_COUNT

  return (
    <Stack gap="250">
      {options.length > SEARCHABLE_COUNT && (
        <SearchInput
          tone="line"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          submit={false}
          placeholder={t("brandFacet.searchPlaceholder")}
          aria-label={t("brandFacet.searchPlaceholder")}
        />
      )}

      <Stack gap="50">
        {visible.map((option) => (
          <Checkbox
            key={option.value}
            checked={selected.includes(option.value)}
            onChange={() => onToggle(option.value)}
            className={ROW}
            label={
              <span className="flex min-w-0 items-center gap-200">
                <Text as="span" variant="bodySm" family="text" tone="default" truncate className="min-w-0 flex-1">
                  {option.label}
                </Text>
                <Text as="span" variant="caption" tone="muted" numeric className="shrink-0">
                  {option.count.toLocaleString("es-MX")}
                </Text>
              </span>
            }
          />
        ))}

        {visible.length === 0 && (
          <Text as="p" variant="bodySm" tone="muted">
            {t("brandFacet.noMatches")}
          </Text>
        )}

        {!forceExpand && !term && matches.length > COLLAPSED_COUNT && (
          <Button
            variant="quiet"
            onClick={() => setShowAll((value) => !value)}
            className="text-fg-brand font-text text-copy -mx-200 justify-start gap-100 px-200 py-100 font-normal"
          >
            <ChevronIcon size={12} className={`transition-transform ${showAll ? "-rotate-90" : "rotate-90"}`} />
            {showAll ? t("brandFacet.showLess") : t("brandFacet.showMore", { count: hiddenCount })}
          </Button>
        )}
      </Stack>
    </Stack>
  )
}
